import {
  forwardRef,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
} from "react";

import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/Input";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { filesFromDrop, isUploadableFile } from "./filePickerAccept";

export interface FilePickerProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "onChange" | "value" | "defaultValue"> {
  /** Visible label above the control; omit when using an external `Label`. */
  label?: string;
  hoverHint?: string;
  onChange?: (file: File | null, event: ChangeEvent<HTMLInputElement>) => void;
  browseLabel?: string;
  emptyLabel?: string;
}

function selectedFileLabel(files: FileList | null, multiple: boolean | undefined, emptyLabel: string): string {
  if (files == null || files.length === 0) {
    return emptyLabel;
  }
  if (multiple && files.length > 1) {
    return `${files.length} files selected`;
  }
  return files[0]?.name ?? emptyLabel;
}

function assignInputFiles(input: HTMLInputElement, files: File[]): boolean {
  if (typeof DataTransfer === "undefined") {
    return false;
  }
  const transfer = new DataTransfer();
  for (const file of files) {
    transfer.items.add(file);
  }
  input.files = transfer.files;
  return true;
}

export const FilePicker = forwardRef<HTMLInputElement, FilePickerProps>(
  (
    {
      id: idProp,
      label,
      hoverHint,
      accept,
      disabled,
      className,
      onChange,
      browseLabel = "Browse…",
      emptyLabel = "No file chosen",
      name,
      multiple,
      ...rest
    },
    ref,
  ) => {
    const autoId = useId();
    const id = idProp ?? autoId;
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [selectedLabel, setSelectedLabel] = useState(emptyLabel);
    const [dragActive, setDragActive] = useState(false);

    const setRefs = (element: HTMLInputElement | null): void => {
      inputRef.current = element;
      if (typeof ref === "function") {
        ref(element);
      } else if (ref != null) {
        ref.current = element;
      }
    };

    const openPicker = (): void => {
      if (disabled) {
        return;
      }
      inputRef.current?.click();
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
      const input = event.target;
      const rawFiles = input.files;
      if (rawFiles != null && rawFiles.length > 0) {
        const uploadable = Array.from(rawFiles).filter((file) => isUploadableFile(file));
        if (uploadable.length !== rawFiles.length) {
          assignInputFiles(input, uploadable);
        }
      }
      const file = input.files?.[0] ?? null;
      setSelectedLabel(selectedFileLabel(input.files, multiple, emptyLabel));
      onChange?.(file, event);
      if (event.target.files == null || event.target.files.length === 0) {
        setSelectedLabel(emptyLabel);
      }
    };

    const handleFilenameKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
      if (disabled) {
        return;
      }
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openPicker();
      }
    };

    const hasFilePayload = (event: DragEvent<HTMLDivElement>): boolean => {
      return Array.from(event.dataTransfer.types).some((type) => type.toLowerCase() === "files");
    };

    const handleDragEnter = (event: DragEvent<HTMLDivElement>): void => {
      if (disabled || !hasFilePayload(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setDragActive(true);
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
      if (disabled || !hasFilePayload(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "copy";
      setDragActive(true);
    };

    const handleDragLeave = (event: DragEvent<HTMLDivElement>): void => {
      event.preventDefault();
      event.stopPropagation();
      const related = event.relatedTarget;
      if (related instanceof Node && event.currentTarget.contains(related)) {
        return;
      }
      setDragActive(false);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
      if (disabled) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      const input = inputRef.current;
      if (input == null) {
        return;
      }
      const dropped = filesFromDrop(event.dataTransfer.files, { accept, multiple });
      if (dropped.length === 0) {
        return;
      }
      if (!assignInputFiles(input, dropped)) {
        return;
      }
      handleChange({
        target: input,
        currentTarget: input,
      } as ChangeEvent<HTMLInputElement>);
    };

    return (
      <div className={className}>
        {label != null ? (
          <div className="mb-1 flex items-center gap-1">
            <Label htmlFor={id}>{label}</Label>
            {hoverHint != null ? <HoverHintWithGlyph hoverHint={hoverHint} /> : null}
          </div>
        ) : null}
        <div
          className={cn(
            "flex h-10 w-full min-w-[12rem] items-stretch overflow-hidden rounded-md border bg-white text-sm",
            dragActive
              ? "border-brand-500 bg-brand-50 ring-1 ring-brand-500"
              : "border-slate-300 focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500",
            disabled && "cursor-not-allowed bg-slate-100",
          )}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={setRefs}
            id={id}
            type="file"
            accept={accept}
            disabled={disabled}
            name={name}
            multiple={multiple}
            className="sr-only pointer-events-none"
            {...rest}
            onChange={handleChange}
          />
          <button
            type="button"
            disabled={disabled}
            onClick={openPicker}
            className="shrink-0 border-r border-slate-300 bg-slate-50 px-3 text-slate-700 transition-colors hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-slate-50"
          >
            {browseLabel}
          </button>
          <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled || undefined}
            onClick={openPicker}
            onKeyDown={handleFilenameKeyDown}
            className={cn(
              "flex min-w-0 flex-1 items-center px-3 text-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500",
              disabled ? "cursor-not-allowed" : "cursor-pointer",
            )}
          >
            <span className="truncate">{selectedLabel}</span>
          </div>
        </div>
      </div>
    );
  },
);
FilePicker.displayName = "FilePicker";
